import { useState } from 'react';
import { store } from '../store/store';
import {
    CancellationFeeStep,
    InvoiceCorrectionAfterCancel,
    bookingWriteApiSlice,
} from '../api/booking/bookingWriteApiSlice';
import { getApiErrorField } from '../utils/apiError';

/**
 * The cancellation-fee step of a staff cancel (M10, W1-6, #3042), shared by the
 * detail pages that cancel a booking.
 *
 * `openIfFeeApplies` reads what the booking's rule charges now. When a fee is
 * due, a rule binds whose fee could not be computed, or the cancel will
 * correct an issued invoice (D4 = A), the fee dialog takes over from the plain
 * cancel dialog (returns true). Otherwise the page opens
 * its usual dialog (false). A failed read also returns false: the server is
 * the gate, and refuses a cancel that owes a fee without the operator's choice
 * (`cancelErrorMessageKey` says so on screen).
 */
export const useCancellationFeeStep = (orderId: string) => {
    const [quote, setQuote] = useState<CancellationFeeStep | null>(null);

    const openIfFeeApplies = async (): Promise<boolean> => {
        try {
            const result = await store
                .dispatch(bookingWriteApiSlice.endpoints.getCancellationFee.initiate(orderId, { forceRefetch: true }))
                .unwrap();
            if (result.kind === 'fee' || result.kind === 'undetermined' || (result.kind === 'free' && result.liveInvoiceNumber)) {
                setQuote(result);
                return true;
            }
        } catch {
            // Falls through to the plain dialog; see above.
        }
        return false;
    };

    return {
        quote,
        open: quote !== null,
        close: () => setQuote(null),
        openIfFeeApplies,
    };
};

/** The message for a failed cancel: a fee that appeared or changed meanwhile, or the generic one. */
export const cancelErrorMessageKey = (error: unknown): string => {
    const reason = getApiErrorField(error, 'reason');
    if (reason === 'fee_changed' || reason === 'fee_choice_required') {
        return 'booking_details.cancellation_fee.changed_message';
    }
    return 'booking_details.cancel_booking.error_message';
};

/**
 * What the snackbar adds about the invoice the cancel corrected, or null when
 * it touched none. A failed correction is a warning: the booking is cancelled,
 * the invoice still claims the booking, and the operator corrects it by hand.
 */
export const invoiceCorrectionMessage = (
    t: (key: string, options?: Record<string, unknown>) => string,
    correction: InvoiceCorrectionAfterCancel | undefined,
): { text: string, failed: boolean } | null => {
    if (!correction) return null;
    if (!correction.ok) {
        return {
            text: t('booking_details.cancellation_fee.invoice_failed', { number: correction.originalNumber }),
            failed: true,
        };
    }
    return {
        text: correction.replacementNumber
            ? t('booking_details.cancellation_fee.invoice_replaced', {
                number: correction.originalNumber,
                cancellation: correction.cancellationNumber,
                replacement: correction.replacementNumber,
            })
            : t('booking_details.cancellation_fee.invoice_canceled', {
                number: correction.originalNumber,
                cancellation: correction.cancellationNumber,
            }),
        failed: false,
    };
};
